import dayjs from "dayjs";
import ApiContentSearch from "../../api/content-search";
import { canOpen } from "../../common/permissions/screens";

// LF360-2030: what the search widget can search, and how. A content type is
// one entry here — its endpoints, its filters in the order the menus show
// them, and how a result reads — so the widget itself never names one.

const assetHost = () => process.env.REACT_APP_DEV_URL || document.location.origin;

// A value with no name is still offered, under a word rather than its id.
const option = (id, label, unnamed) => ({ value: id, label: label || unnamed });

// No Status filter: only switched-on content is offered — a switched-off
// article or FitFact is not delivered, so there is nothing to pick it for. The
// backend searches switched-on content when no status is asked for.

const AUTHOR_FILTER = {
  key: "authorIds",
  label: "Author",
  optionsOf: (o) => (o.authors || []).map((a) => option(a.id, a.name, "Unnamed author")),
};

const CATEGORY_FILTER = {
  key: "categoryIds",
  label: "Category",
  optionsOf: (o) => (o.categories || []).map((c) => option(c.id, c.name, "Unnamed category")),
};

export const STATUS_FILTER = {
  key: "statuses",
  label: "Status",
  optionsOf: (o) => (o.statuses || []).map((status) => option(status.id, status.name, "Unknown status")),
};

/** A stable hue per word, for tiles with no picture. */
export const hueOf = (text = "") =>
  [...String(text)].reduce((hash, char) => (hash * 31 + char.charCodeAt(0)) % 360, 7);

const letterOf = (text) => (String(text || "").trim()[0] || "?").toUpperCase();

const updatedLabel = (value) => (value ? dayjs(value).format("MMM D, YYYY") : "—");

export const CONTENT_TYPES = {
  articles: {
    key: "articles",
    label: "Articles",
    noun: { one: "article", many: "articles" },
    searchedField: "title",
    search: ApiContentSearch.searchArticles,
    facets: ApiContentSearch.articleFacets,
    filterOptions: ApiContentSearch.articleFilters,
    filters: [
      { key: "tagIds", label: "Tags", optionsOf: (o) => (o.tags || []).map((t) => option(t.id, t.name, "Unnamed tag")) },
      CATEGORY_FILTER,
      AUTHOR_FILTER,
      {
        key: "sources",
        label: "Source",
        optionsOf: (o) => (o.sources || []).map((s) => option(s.id, s.name, s.id)),
      },
    ],
    titleOf: (item) => item.title || "Untitled article",
    thumbnailOf: (item) => ({
      imageUrl: item.hasThumbnail ? `${assetHost()}${item.thumbnailUrl}` : null,
      letter: letterOf(item.category?.name || item.title),
      hue: hueOf(item.category?.name || item.title),
    }),
    previewOf: (item) => ({
      excerpt: item.excerpt,
      tags: (item.tags || []).map((t) => t.name),
      rows: [
        ["Category", item.category?.name || "—"],
        ["Author", item.author?.name || "—"],
        ["Source", item.source === "external" ? "External" : "Internal"],
        ["Updated", updatedLabel(item.updatedAt)],
      ],
    }),
  },

  fitFacts: {
    key: "fitFacts",
    label: "FitFacts",
    noun: { one: "FitFact", many: "FitFacts" },
    // A FitFact has no title: the keyword is matched against its text.
    searchedField: "text",
    search: ApiContentSearch.searchFitFacts,
    facets: ApiContentSearch.fitFactFacets,
    filterOptions: ApiContentSearch.fitFactFilters,
    filters: [
      { key: "tagIds", label: "Tags", optionsOf: (o) => (o.tags || []).map((t) => option(t.id, t.name, "Unnamed tag")) },
      {
        key: "urlIds",
        label: "Source URL",
        optionsOf: (o) => (o.urls || []).map((u) => option(u.id, u.title || u.url, "Unnamed URL")),
      },
      {
        key: "citationIds",
        label: "Source citation",
        optionsOf: (o) => (o.citations || []).map((c) => option(c.id, c.text, "Empty citation")),
      },
      CATEGORY_FILTER,
      AUTHOR_FILTER,
    ],
    titleOf: (item) => item.text || "Empty FitFact",
    // A FitFact has no picture: its colour and its first letter (the ticket).
    thumbnailOf: (item) => ({
      imageUrl: null,
      color: item.color || null,
      letter: letterOf(item.text),
      hue: hueOf(item.text),
    }),
    previewOf: (item) => ({
      excerpt: null,
      tags: (item.tags || []).map((tag) => tag.name),
      rows: [
        ["Category", item.category?.name || "—"],
        ["Author", item.author?.name || "—"],
        ["Source URL", item.url?.title || item.url?.url || "—"],
        ["Citation", item.citation?.text || "—"],
        ["Updated", updatedLabel(item.updatedAt)],
      ],
    }),
  },
};

export const SPOTLIGHT_TYPES = [
  "articles", "fitFacts", "tasks", "plans", "lifePoints", "categories", "tags", "scenarios", "onboardings", "rules", "subjects", "settings",
];

// LF360-2069: the statuses each type has, as the server filters them. A task,
// plan or onboarding can be a draft ("Pending changes", as the portal labels
// it); a type missing here has no status.
const STATUS_LABELS = { active: "Active", pending: "Pending changes", inactive: "Inactive" };
const STATUSES_OF = {
  articles: ["active", "inactive"],
  fitFacts: ["active", "inactive"],
  tasks: ["active", "pending", "inactive"],
  plans: ["active", "pending", "inactive"],
  onboardings: ["active", "pending", "inactive"],
  scenarios: ["active", "inactive"],
};
const statusOptions = (ids = []) => ids.map((id) => ({ id, name: STATUS_LABELS[id] }));
// The types whose items carry a category, so a result row shows one.
const WITH_CATEGORY = ["tasks", "lifePoints"];

/**
 * A query for one type out of the All scope's: only the statuses that type has.
 * null when the chosen statuses leave nothing of it to find, so it is not asked.
 */
const queryForType = (key, query) => {
  const requested = Array.isArray(query.statuses)
    ? query.statuses
    : String(query.statuses || "").split(",").filter(Boolean);
  if (!requested.length) {return query;}
  const { statuses: unusedStatuses, ...rest } = query;
  if (!STATUSES_OF[key]) {return rest;}
  const kept = requested.filter((status) => STATUSES_OF[key].includes(status));

  return kept.length ? { ...rest, statuses: kept } : null;
};

const BASIC_TYPES = [
  ["tasks", "Tasks", "task", "/content/tasks/"],
  ["plans", "Plans", "plan", "/content/plans/"],
  ["lifePoints", "LifePoints", "LifePoint", "/content/lifepoints"],
  ["categories", "Categories", "category", "/content/categories"],
  ["tags", "Tags", "tag", "/content/tags"],
  ["scenarios", "Scenarios", "scenario", "/content/scenarios/"],
  ["onboardings", "Onboarding", "onboarding", "/content/onboardings/"],
  ["rules", "Rules", "rule", "/content/rules/"],
];

BASIC_TYPES.forEach(([key, label, singular, path]) => {
  CONTENT_TYPES[key] = {
    key,
    label,
    noun: { one: singular, many: label.toLowerCase() },
    searchedField: "name",
    search: (query) => ApiContentSearch.searchSpotlight(key, query),
    facets: (query) => ApiContentSearch.spotlightFacets(key, query),
    filterOptions: async () => ({ data: { statuses: statusOptions(STATUSES_OF[key]) } }),
    filters: [],
    supportsStatus: Boolean(STATUSES_OF[key]),
    hasCategory: WITH_CATEGORY.includes(key),
    titleOf: (item) => item.title || `Untitled ${singular}`,
    thumbnailOf: (item) => ({ imageUrl: null, letter: letterOf(item.title), hue: hueOf(label) }),
    previewOf: (item) => ({ excerpt: null, tags: [], rows: [["Updated", updatedLabel(item.updatedAt)]] }),
    path,
  };
});

CONTENT_TYPES.subjects = {
  key: "subjects", label: "Subjects", noun: { one: "subject", many: "subjects" }, searchedField: "name or email",
  search: ApiContentSearch.searchSubjects,
  facets: ApiContentSearch.subjectFacets,
  filterOptions: async () => ({ data: {} }), filters: [], supportsStatus: false, hasCategory: false,
  titleOf: (item) => item.title, thumbnailOf: (item) => ({ imageUrl: null, letter: letterOf(item.title), hue: hueOf("Subjects") }),
  previewOf: (item) => ({ excerpt: item.email, tags: [], rows: [["Status", item.status || "—"], ["Updated", updatedLabel(item.updatedAt)]] }),
  path: "/subject-management/subject/",
  supportsDate: true,
};

const SETTING_ITEMS = [
  ["Project settings", "/administration/project-settings"], ["Profiles", "/administration/profiles"],
  ["Enrollment", "/administration/enrollment"], ["Cohorts", "/administration/cohorts"],
  ["Functionality", "/administration/functionality"], ["Notifications", "/administration/notifications"],
  ["LifePoints settings", "/administration/lifepoints"], ["Mobile home page", "/administration/mobile-home-page"],
  ["LifeBot", "/administration/lifebot"], ["Legal documents", "/administration/legal-documents"], ["Sites", "/administration/sites"],
].map(([title, path]) => ({ id: path, title, path }));

CONTENT_TYPES.settings = {
  key: "settings", label: "Settings", noun: { one: "setting", many: "settings" }, searchedField: "setting name",
  search: async ({ q = "" }, unusedTypes, access) => ({ data: { items: SETTING_ITEMS.filter((item) => access && canOpen(access, item.path) && item.title.toLowerCase().includes(q.toLowerCase())), hasMore: false, nextCursor: null } }),
  facets: async ({ q = "" }, unusedTypes, access) => ({ data: { total: SETTING_ITEMS.filter((item) => access && canOpen(access, item.path) && item.title.toLowerCase().includes(q.toLowerCase())).length, counts: {}, updated: [] } }),
  filterOptions: async () => ({ data: {} }), filters: [], supportsStatus: false, hasCategory: false,
  titleOf: (item) => item.title, thumbnailOf: (item) => ({ imageUrl: null, letter: letterOf(item.title), hue: hueOf("Settings") }),
  previewOf: () => ({ excerpt: "Project setting", tags: [], rows: [] }),
  supportsDate: false,
};

/**
 * LF360-2069 — where a Spotlight result opens, within the project: its own path
 * (a setting), its type's page plus its id, or the type's list where items
 * have no page of their own. An article or FitFact opens as its list opens it,
 * to read rather than to edit.
 */
export const resultPathOf = (item) => {
  if (item.path) {return item.path;}
  if (item.contentType === "articles") {return `/content/articles/${item.id}`;}
  if (item.contentType === "fitFacts") {return `/content/fitfacts/${item.id}`;}
  const { path } = CONTENT_TYPES[item.contentType];

  return path.endsWith("/") ? `${path}${item.id}` : path;
};

// The dashboard's initial Spotlight scope.  It deliberately has no combined
// filter menu: Article and FitFact filters are different vocabularies.  A
// user narrows to a type before applying type-specific filters.
CONTENT_TYPES.all = {
  key: "all",
  label: "All",
  noun: { one: "item", many: "content items" },
  searchedField: "title or text",
  filters: [],
  filterOptions: async () => ({ data: { statuses: statusOptions(["active", "pending", "inactive"]) } }),
  search: async (query, availableTypes = SPOTLIGHT_TYPES, access) => {
    const searchable = availableTypes.filter((key) => key !== "all");
    const cursors = query.cursor ? JSON.parse(query.cursor) : {};
    const settled = await Promise.allSettled(searchable.map((key) => {
      // Each type is asked only for the statuses it has (see queryForType).
      const own = queryForType(key, query);
      if (cursors[key] === false || !own) {return Promise.resolve({ data: { items: [] } });}

      return key === "settings"
        ? CONTENT_TYPES[key].search({ ...own, limit: 10 }, undefined, access)
        : CONTENT_TYPES[key].search({ ...own, cursor: cursors[key] || undefined, limit: 10 });
    }));
    const failed = settled.find((result) => result.status === "rejected");
    if (failed) {throw failed.reason;}
    const responses = settled.map((result) => result.status === "fulfilled" ? result.value : { data: { items: [] } });
    const mark = (key, response) => (response.data?.items || []).map((item) => ({ ...item, contentType: key }));
    const next = Object.fromEntries(searchable.map((key, index) => [key,
      responses[index].data?.hasMore ? responses[index].data.nextCursor : false]));
    const hasMore = Object.values(next).some(Boolean);

    return {
      data: {
        items: responses.flatMap((response, index) => mark(searchable[index], response)),
        // The two resources have independent cursors.  The All preview is
        // intentionally bounded; select a type to browse its full result set.
        hasMore,
        nextCursor: hasMore ? JSON.stringify(next) : null,
      },
    };
  },
  facets: async (query, availableTypes = SPOTLIGHT_TYPES, access) => {
    const searchable = availableTypes.filter((key) => key !== "all");
    const settled = await Promise.allSettled(searchable.map((key) => {
      const own = queryForType(key, query);
      if (!own) {return Promise.resolve({ data: { total: 0 } });}

      return key === "settings"
        ? CONTENT_TYPES[key].facets(own, undefined, access) : CONTENT_TYPES[key].facets(own);
    }));
    const failed = settled.find((result) => result.status === "rejected");
    if (failed) {throw failed.reason;}
    const responses = settled.map((result) => result.status === "fulfilled" ? result.value : { data: { total: 0 } });

    return { data: { total: responses.reduce((total, response) => total + (response.data?.total || 0), 0), counts: {}, updated: [] } };
  },
  titleOf: (item) => CONTENT_TYPES[item.contentType].titleOf(item),
  thumbnailOf: (item) => CONTENT_TYPES[item.contentType].thumbnailOf(item),
  previewOf: (item) => ({
    ...CONTENT_TYPES[item.contentType].previewOf(item),
    rows: [["Type", CONTENT_TYPES[item.contentType].label], ...CONTENT_TYPES[item.contentType].previewOf(item).rows],
  }),
};
